// External libraries
import React, { useEffect, useMemo, useRef, useState } from "react";
import { GeoId } from "../../types/geo.types";

// Components
import { InfoPanel, Legend } from "../../ui-elements";
import { CifTypeSwitch } from ".";
import { Typography } from "../Shadcn/Typography";

//icons
import { LuArrowUpRight } from "react-icons/lu";
import { LuArrowDownLeft } from "react-icons/lu";
import { ChevronDown, ChevronUp } from "@carbon/icons-react";
// Utilities
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { isAddedToCompare, subGeoLevel } from "../../helpers";
import { checkboxState, CIFType, compareListState, expandedPresenceConfigState, geoState, LegendGeoLevel, metricState, poiMapColorState, ptypeState, selAggLayerState, selectedGeoDropdownState, selectedGeoNameState, selectedTabState, showDrawerState } from "../../states";
import * as Constants from '../../utils/constants/Constants';
import { AddToCompare } from "./AddToCompare";
import { useActivateTooltip } from "../../helpers/useActivateTooltip";
import { createSearchParams, useNavigate, useSearchParams } from "react-router-dom";
import { RouteConstants } from "../../constants";

interface TopDistrictsProps {
    cifType: CIFType[];
    expandedTopDistricts: boolean;
    topfiveregions: any[];
    setExpandedTopDistricts: (value: boolean) => void;
    selCatchmentType: any;
    dataLayersGroup?: any[];
    legendShimmer?: boolean;
    geoCode?: GeoId;
    isPoiOverlapped?: boolean;
}

interface DistrictItemProps {
    item: any;
    index: number;
    handleNavigation: (geoData: any) => void;
    compareList: any;
}

const DistrictItem = ({ item, index, handleNavigation, compareList }: DistrictItemProps) => {
    const textRef = useRef<HTMLDivElement>(null);
    const showTooltip = useActivateTooltip(textRef);
    const isChecked = useRecoilValue(checkboxState);
    const pType = useRecoilValue(ptypeState);
    return (
        <li key={`${item.geoLevel}-${item.district || item.sub_district}-${index}`} className="district-item list-none">
            <div className="district-content secondary-hoverTop relative px-4">
                <div
                    ref={textRef}
                    title={showTooltip || isChecked?.catchments ? `${index + 1}. ${item.regionName}` : undefined}
                    className="text-left w-7/10 truncate mbp-tooltip-parent text-xs cursor-pointer font-medium"
                    onClick={() => handleNavigation(item)}
                >
                    {`${index + 1}. `}<span className="underline">{item.regionName}</span>
                </div>
                <AddToCompare
                    isAddedToCompare={isAddedToCompare(item.geoLevel === 'district' ? item.district : item?.geoLevel == "pincode" ? item?.pincode : item.geoLevel === 'sub_district' ? item.sub_district : item.geoLevel === 'village' ? item.village : item?.geoLevel === "catchment" ? item?.catchment : item.sub_district, compareList, pType)}
                    geoId={item.geoLevel === 'district' ? item.district : item?.geoLevel == "pincode" ? item?.pincode : item.geoLevel === 'sub_district' ? item.sub_district : item.geoLevel === 'village' ? item.village : item?.geoLevel === "catchment" ? item?.catchment : item.sub_district}
                    level={item?.geoLevel === "catchment" ? -1 : item.geoLevelNumber}
                    parent_id={item?.geoLevel == "catchment" ? null : item.district}
                    count={item.geoLevel === "district"
                        ? compareList?.districts?.length :
                        item.geoLevel === "sub_district" ?
                            compareList?.subdistricts?.length :
                            item.geoLevel === "village" ?
                                compareList?.villages?.length :
                                item?.geoLevel === "catchment" && pType === "Branch" ? compareList?.Branch?.length : item?.geoLevel === "catchment" && pType === "Partner" ? compareList?.Partner?.length : item?.geoLevel === "catchment" && pType === "Relationship Manager" ? compareList?.RelationshipManager?.length : compareList?.pincodes?.length}
                    tooltipPosition='right'
                />
            </div>
        </li>
    );
};

export const TopDistricts = ({ cifType, expandedTopDistricts, setExpandedTopDistricts, topfiveregions, selCatchmentType, dataLayersGroup = [], legendShimmer = false, geoCode = 0, isPoiOverlapped = false }: TopDistrictsProps) => {
    const [selectedGeo, setselectedGeo] = useRecoilState(geoState);
    const [shimmer, setShimmer] = useState(false);
    const setExpandedPresenceConfig = useSetRecoilState(expandedPresenceConfigState);
    const compareList = useRecoilValue(compareListState);
    const selectedTab = useRecoilValue(selectedTabState);
    const isChecked = useRecoilValue(checkboxState);
    const mapColors = useRecoilValue(poiMapColorState);
    const selectedGeoName = useRecoilValue(selectedGeoNameState);
    const [open, setOpen] = useRecoilState(showDrawerState);
    const isInitialMount = React.useRef(true);
    const [searchParams] = useSearchParams();
    const catchment = searchParams.get("catchment");
    const navigate = useNavigate();
    const selMetric = useRecoilValue(metricState);
    const poiColors = useRecoilValue(poiMapColorState);
    const selAggLayer = useRecoilValue(selAggLayerState);
    const subLevel = subGeoLevel(selectedGeo, selectedTab, catchment) as LegendGeoLevel;
    const selectedGeoDropdown = useRecoilValue(selectedGeoDropdownState);

    // const geoCode = useMemo(() => {
    //     const { country, state, district, sub_district, village } = selectedGeo;
    //     const geo_id = village || sub_district || district || state || country;
    //     return Number(geo_id);
    // }, [selectedGeo]);

    const geoLevelForCompare = useMemo(() => {
        const { country, state, district } = selectedGeo;
        if (selectedGeoDropdown?.geotype === "pincode") return "pincodes"
        if (country == geoCode || state == geoCode) return 'districts';
        else if (district == geoCode) return 'subdistricts';
        else return "villages"
    }, [geoCode]);
    const topDistricts = geoLevelForCompare === 'subdistricts' && !isChecked?.catchments ?
        selectedTab === 5 ? topfiveregions.filter(item => item.geoLevel === 'pincode') :
            topfiveregions.filter(item => item.geoLevel === 'sub_district') :
        topfiveregions;

    const onExpandTopDistricts = () => {
        if (expandedTopDistricts) {
            setExpandedTopDistricts(false);
            // setExpandedPresenceConfig(0);
        } else {
            setExpandedTopDistricts(true);
            setExpandedPresenceConfig(null);
        }
    }

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        if (open) {
            setExpandedTopDistricts(false);
        }
        else {
            setExpandedTopDistricts(true);
        }
    }, [open]);

    const getTopRegionsTitle = () => {
        let regionType = '';

        if (isChecked?.catchments) {
            regionType = catchment ? 'Villages' : selCatchmentType?.title + (selCatchmentType?.title === 'Branch' ? 'es' : 's')
        } else if (selectedGeo?.sub_district) {
            regionType = 'Villages';
        } else if (selectedGeo?.district) {
            regionType = selectedTab === 5 ? 'PIN Code' : 'Sub-districts';
        } else {
            regionType = 'Districts';
        }

        const locationSuffix = !isChecked?.adjacency ? ` in ${mapColors?.rootProperties?.geoName || selectedGeoName}` : '';
        return `Top ${regionType}${locationSuffix}`;
    };

    const handleNavigation = (geoData: any) => {
        if (geoData?.geoLevel === 'catchment') {
            searchParams.set('catchment', geoData?.catchment);
            navigate({
                pathname: RouteConstants.catchments,
                search: `?${searchParams.toString()}`,
            });
            return;
        }
        const { country, state, district, sub_district, village, pincode } = geoData;
        const updatedGeo = { country, state, district, sub_district: sub_district || pincode, village };
        setselectedGeo(updatedGeo);

        if (catchment) {
            setTimeout(() => {
                const newSearchParams = createSearchParams(updatedGeo);
                navigate(RouteConstants.root + "?" + newSearchParams);
            }, 0);
        }
    }

    const topDistrictsHeight = expandedTopDistricts ? '26.788vh' : '6.5vh';
    const hasDataLayers = !(selectedGeo.village && selAggLayer === null);
    const hasTopDistricts = topDistricts.length > 0 && (!selectedGeo?.village || catchment);

    return (
        <div className="w-full flex flex-col justify-end">
            {/* Data Layers Section - First child, always on top */}

            <div className="bg-white border-t">
                {dataLayersGroup.map((layer: any) => (
                    <React.Fragment key={layer.type}>
                        <div className="flex flex-col px-4 py-3 relative gap-1">
                            <CifTypeSwitch
                                layer={layer}
                                isChecked={false}
                                cifType={cifType}
                                handleToggleChange={() => { }}
                                disabled={false}
                                selMetric={selMetric}
                                isPoiOverlapped={isPoiOverlapped}
                                catchment={catchment}
                                customTooltip=""
                            />
                            <Legend
                                data={poiColors?.legendProperties?.[selectedGeo?.village && selAggLayer?.key === 'hexgrid' ? 'hex' : subLevel as LegendGeoLevel]}
                                legendShimmer={legendShimmer}
                                geoCode={geoCode}
                                mapColorRootProps={poiColors?.rootProperties}
                            />
                        </div>
                        {/* <hr className="p-0 m-0 margin-left-right-5 border-dotted" /> */}
                    </React.Fragment>
                ))}
            </div>

            {/* TopDistricts content */}
            {hasTopDistricts
                && <div className="border-t">
                    {(shimmer)
                        ? <div className='w-full bottom-0 relative' style={{ height: expandedTopDistricts ? '26.788vh' : '6.5vh' }}>
                            <div className="flex justify-center items-center shimmer absolute" ></div>
                        </div>
                        : <React.Fragment>
                            <div className="flex justify-between items-center pl-4 pr-4 pt-2 pb-2 bg-white">
                                <div
                                    className='flex flex-row justify-start items-center relative w-[90%]'
                                    title={getTopRegionsTitle()}
                                >
                                    <Typography variant="b2" className="font-semibold truncate text-start font-brand">
                                        {getTopRegionsTitle()}
                                    </Typography>
                                    {/* <div className="helper-text padding-1 top-0 left-0">
                                        <Body
                                            type={BodyType.p2}
                                            color={BodyColor.white}
                                            classname="p-0 text-start"
                                        >
                                            {`Top ${selectedGeo?.sub_district ? "Villages" : selectedGeo?.district ? selectedTab === 5 ? 'PIN Code' : "Sub-districts" : 'Districts'}${!isChecked?.adjacency ? ` in ${selectedGeoName}` : ''}`}
                                        </Body>
                                    </div> */}
                                    {selectedGeo?.district ?
                                        // To be changed to a better name, currently we have same text
                                        <InfoPanel
                                            text={Constants.iButtonText.homePage.leftPanel.topsubDistricts['PoI']}
                                            classname='icon-gray-0 text-base ml-2 mr-4'
                                            upward={!expandedTopDistricts}
                                        /> :
                                        <InfoPanel
                                            text={Constants.iButtonText.homePage.leftPanel.topDistricts['PoI']}
                                            classname='icon-gray-0 text-base ml-2 mr-4'
                                            upward={!expandedTopDistricts}
                                        />
                                    }
                                </div>
                                {/* <Heading
                                        title='BETA'
                                        type={TypographyType.h6}
                                        colour={TypographyColor.primary}
                                        classname={`text-start padding-top-bottom-1 padding-left-right-4 rounded-pill ff-poppins-bold ${cifType === 'CTV' ? 'bg-ctv color-ctv' : 'bg-poi color-poi'}`}
                                    /> */}
                                {/* <Heading
                                    title={open ? "See less" : "See More"}
                                    type={TypographyType.h6}
                                    colour={TypographyColor.primary}

                                    onClick={() => setOpen(!open)}
                                /> */}
                                {/* <div className="d-flex flex-column align-items-center justify-content-center position-relative"> */}

                                {/* {open ? <LuArrowDownLeft onClick={() => setOpen(!open)} /> : <LuArrowUpRight onClick={() => setOpen(!open)} />} */}

                                {/* <div style={{ position: 'absolute', bottom: '140%', left: '50%', transform: 'translateX(-50%)', zIndex: 9999 }}>
                                        <div
                                            style={{
                                                position: 'relative',
                                                backgroundColor: 'white',
                                                padding: '10px 14px',
                                                borderRadius: '8px',
                                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                                fontSize: '15px',
                                                textAlign: 'center',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            <div>hi</div>
                                            <div
                                                style={{
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: '100%',
                                                    left: '50%',
                                                    transform: 'translateX(-50%)',
                                                    width: 0,
                                                    height: 0,
                                                    borderLeft: '6px solid transparent',
                                                    borderRight: '6px solid transparent',
                                                    borderTop: '6px solid white',
                                                    borderBottomLeftRadius: '5px',
                                                    borderBottomRightRadius: '5px'
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div> */}


                                <button className="cursor-pointer text-foreground shrink-0" onClick={() => onExpandTopDistricts()}>
                                    {expandedTopDistricts ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
                                </button>
                            </div>
                            {expandedTopDistricts &&
                                <ul
                                    className={`list-none m-0 py-1 border-t ${cifType.toString() === 'CTV' ? 'bg-ctv' :
                                        cifType.toString() === 'PoI' ? 'bg-poi' :
                                            cifType.toString() === 'EH' ? 'bg-eh' : 'bg-white'}`}
                                >
                                    {topDistricts.map((item: any, index) => (
                                        <DistrictItem
                                            key={`${item.geoLevel}-${item.district || item.sub_district}-${index}`}
                                            item={item}
                                            index={index}
                                            handleNavigation={handleNavigation}
                                            compareList={compareList}

                                        />
                                    ))}
                                </ul>}
                            {expandedTopDistricts &&
                                <button
                                    className="flex flex-row items-center px-5 pb-2 cursor-pointer text-brand"
                                    onClick={() => setOpen(!open)}
                                >
                                    <Typography variant="b3" className="flex items-center gap-1 underline pt-2 text-brand">
                                        {open ? (<>See less <LuArrowDownLeft /></>) : (<>See All <LuArrowUpRight /></>)}
                                    </Typography>
                                </button>}
                        </React.Fragment>
                    }
                </div>
            }
        </div>
    )
}



